'use client';

import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useLocale, useTranslations } from 'next-intl';
import { useRef, useState } from 'react';

import { Button } from '@commons/components/Button';
import Carousel from '@commons/components/Carousel';
import LoadingSkeleton from '@commons/components/LoadingSkeleton';
import Flex from '@commons/containers/Flex';
import { useMediaQueries } from '@commons/contexts/responsive';
import { generateId } from '@commons/helpers/string';
import { useSize } from '@commons/hooks/useSize';
import PageBuilder from '@commons/pagebuilder';
import { fetchView } from '@commons/services/view';

import type { DrupalInsightNodeProps, DrupalInsightsView } from '.';
import styles from './LatestPopular.module.scss';

/**
 * LatestPopular display of the insights view.
 *
 * Renders a bleeding carousel with the latest or most popular insights.
 */

const LatestPopularDisplay = ({ id, display, settings }: DrupalInsightsView) => {
  const t = useTranslations('views.insights');
  const ref = useRef<HTMLDivElement>(null);
  const size = useSize(ref);
  const locale = useLocale();
  const { minWidth } = useMediaQueries();
  const [activeFilters, setActiveFilters] = useState(settings.filters!);

  const limit = settings.limit ? parseInt(settings.limit, 10) : 10;

  const { data, isLoading } = useQuery({
    queryKey: [id, display, locale, limit, activeFilters],
    queryFn: () =>
      fetchView<DrupalInsightNodeProps>({ id, display, locale, limit, filters: activeFilters }),
    staleTime: 1000 * 60 * 5,
  });

  const isMostPopular = activeFilters.popular === '1';

  const handleLatest = () => {
    setActiveFilters((prev) => ({ ...prev, popular: 'All' }));
  };

  const handleMostPopular = () => {
    setActiveFilters((prev) => ({ ...prev, popular: '1' }));
  };

  const content = data || [];

  const renderedSlides = content.map((node) => {
    const id = generateId(`slide-${node.id}`);
    return <Carousel.Slide key={id}>{PageBuilder.renderNode(node)}</Carousel.Slide>;
  });

  const slidesPerView = minWidth?.xl ? 4.5 : minWidth?.lg ? 2.4 : 1.2;

  const offset = size ? size.x : 0;
  const containerWidth = size ? size.width : undefined;

  const classes = clsx(styles.wrapper, 'w-100');

  return (
    <Flex gap="xl" direction="column" className={classes} ref={ref}>
      <Flex gap="xxs">
        <Button
          active={!isMostPopular}
          variant="tertiary"
          onClick={handleLatest}
          className={styles.button}
        >
          {t('filters.latest')}
        </Button>
        <Button
          active={isMostPopular}
          variant="tertiary"
          onClick={handleMostPopular}
          className={styles.button}
        >
          {t('filters.popular')}
        </Button>
      </Flex>
      {!isLoading ? (
        <Carousel
          role="region"
          aria-label={t(isMostPopular ? 'carousel.popular' : 'carousel.latest')}
          navigation
          slidesOffsetAfter={offset}
          slidesOffsetBefore={offset}
          slidesPerView={slidesPerView}
          className={styles.carousel}
        >
          {renderedSlides}
          <Carousel.Controls
            displayNavigation
            className={styles.controls}
            style={{ left: offset, width: containerWidth }}
          />
        </Carousel>
      ) : (
        <LoadingGrid />
      )}
    </Flex>
  );
};

/**
 * Component to display a grid of loading skeletons.
 */

const LoadingGrid = () => {
  const { ready, minWidth } = useMediaQueries();

  const isMobile = ready && !minWidth.md;

  const skeletons = !isMobile ? 4 : 1;

  return (
    <Flex width={12} gap={{ xs: 'md', xl: 'lg' }}>
      {Array.from({ length: skeletons }).map((_, index) => (
        <Flex.Item key={index} width={{ xs: 12, xl: 4 }}>
          <LoadingSkeleton height={500} />
        </Flex.Item>
      ))}
    </Flex>
  );
};

export default LatestPopularDisplay;
